import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Building2, 
  Vote, 
  Users, 
  CheckCircle2,
  TrendingUp,
  Clock,
  BarChart3
} from "lucide-react";
import { AdminLayout } from "../../components/AdminLayout";
import { getAdminStats, getAdminConsultes } from "../../lib/adminApi";
import { toast } from "sonner";

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    total_entities: 0,
    total_consultes: 0,
    active_consultes: 0,
    closed_consultes: 0,
    scrutinized_consultes: 0,
    total_votes: 0,
    total_census: 0
  });
  const [recentConsultes, setRecentConsultes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, consultesRes] = await Promise.all([
        getAdminStats(),
        getAdminConsultes()
      ]);
      setStats(statsRes.data);
      setRecentConsultes(consultesRes.data.slice(0, 5));
    } catch (error) {
      toast.error(t('errors.loadingData'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, consulta) => {
    if (status === "closed" && consulta?.scrutiny_request_status === "admin_approved") {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-emerald-100" data-testid="status-pending-entity-key">{t('status.pendingEntityKey')}</span>;
    }

    if (status === "closed" && consulta?.scrutiny_request_status) {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-600 text-indigo-100" data-testid="status-scrutiny-requested">{t('status.scrutinyRequested')}</span>;
    }

    const badges = {
      draft: { class: "bg-slate-600 text-slate-200", label: t('status.draft') },
      pending_publication: { class: "bg-orange-500 text-white", label: t('status.pendingPublication') },
      published: { class: "bg-emerald-600 text-emerald-100", label: t('status.voting') },
      pending_closure: { class: "bg-amber-500 text-white", label: t('status.pendingClosure') },
      active: { class: "bg-emerald-600 text-emerald-100", label: t('status.voting') },
      closed: { class: "bg-amber-600 text-amber-100", label: t('status.closed') },
      expired: { class: "bg-red-600 text-red-100", label: t('status.expired') },
      scrutinized: { class: "bg-blue-600 text-blue-100", label: t('status.scrutinized') }
    };
    const badge = badges[status] || badges.draft;
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>{badge.label}</span>;
  };

  const statCards = [
    { 
      label: t('admin.totalEntities'), 
      value: stats.total_entities, 
      icon: Building2,
      color: "bg-blue-500"
    },
    { 
      label: t('admin.totalConsultes'), 
      value: stats.total_consultes, 
      icon: Vote,
      color: "bg-violet-500"
    },
    { 
      label: t('admin.activeConsultes'), 
      value: stats.active_consultes, 
      icon: TrendingUp,
      color: "bg-emerald-500"
    },
    { 
      label: t('admin.closedConsultes'), 
      value: stats.closed_consultes, 
      icon: Clock,
      color: "bg-amber-500"
    },
    { 
      label: t('admin.scrutinizedConsultes'), 
      value: stats.scrutinized_consultes, 
      icon: BarChart3,
      color: "bg-cyan-500"
    },
    { 
      label: t('admin.totalVotes'), 
      value: stats.total_votes, 
      icon: CheckCircle2,
      color: "bg-pink-500"
    },
  ];

  return (
    <AdminLayout>
      <div className="p-4 md:p-8" data-testid="admin-dashboard-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold text-white">
            {t('admin.dashboardTitle')}
          </h1>
          <p className="text-slate-400 mt-1">
            {t('admin.controlPanel')}
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8"
            >
              {statCards.map((stat, index) => (
                <div
                  key={stat.label}
                  className="bg-slate-800 border border-slate-700 rounded-lg p-4"
                  data-testid={`admin-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                    <stat.icon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-slate-400">{stat.label}</p>
                </div>
              ))}
            </motion.div>

            {/* Quick Links & Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Quick Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-slate-800 border border-slate-700 rounded-lg p-6"
              >
                <h2 className="text-lg font-heading font-semibold text-white mb-4">
                  {t('admin.quickAccess')}
                </h2>
                <div className="space-y-3">
                  <Link
                    to="/admin/entities"
                    className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                    data-testid="admin-quick-entities"
                  >
                    <Building2 className="h-5 w-5 text-blue-400" />
                    <span className="text-slate-200">{t('admin.manageEntities')}</span>
                  </Link>
                  <Link
                    to="/admin/consultes"
                    className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                    data-testid="admin-quick-consultes"
                  >
                    <Vote className="h-5 w-5 text-violet-400" />
                    <span className="text-slate-200">{t('admin.viewConsultes')}</span>
                  </Link>
                </div>
              </motion.div>

              {/* Recent Consultes */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-semibold text-white">
                    {t('admin.recentConsultes')}
                  </h2>
                  <Link 
                    to="/admin/consultes"
                    className="text-sm text-amber-400 hover:text-amber-300"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>

                {recentConsultes.length === 0 ? (
                  <div className="text-center py-8">
                    <Vote className="h-12 w-12 text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-400">{t('admin.noConsultes')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentConsultes.map((consulta) => (
                      <div
                        key={consulta.id}
                        onClick={() => navigate('/admin/consultes', { state: { openConsultaId: consulta.id } })}
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors cursor-pointer"
                        data-testid={`admin-consulta-item-${consulta.id}`}
                      >
                        <div>
                          <h3 className="font-medium text-white">{consulta.title}</h3>
                          <p className="text-sm text-slate-400">
                            {consulta.entity_name} · {new Date(consulta.created_at).toLocaleDateString("ca-ES")}
                          </p>
                        </div>
                        {getStatusBadge(consulta.status, consulta)}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
